import "../NotificationDetailsSheet.scss";
import {
    getNotificationDisplayTimestamp,
    getSentOrScheduledTimestampColumnLabelForNotification,
    NotificationStatus,
} from "../../../constants";
import { formatNotificationLocaleDateTime } from "../../helpers";
import "./GeneralNotificationInfo.scss";

/**
 * API channel values for type pills; uppercase in the API; modifiers lowercased.
 */
const CHANNEL_PILL_MODIFIERS = new Set([
    "APP",
    "PUSH",
    "SMS",
    "EMAIL",
    "PHYSICAL",
    "CORRESPONDENCE",
]);

/**
 * Maps v3 notification `status` to pill modifier suffixes (`pill--status-*`).
 * {@link NotificationStatus.CORRESPONDENCE_HRP_UPDATE_*} reuse pending / error styling;
 * {@link NotificationStatus.CANCELED} uses `status-canceled`.
 *
 * @param {unknown} status Raw status from list or details payload.
 * @returns {'status-sent' | 'status-pending' | 'status-error' | 'status-canceled' | 'default'} CSS modifier token for the status pill.
 */
function statusToPillModifier(status) {
    const u = String(status ?? "")
        .trim()
        .toUpperCase();

    switch (u) {
        case NotificationStatus.SENT:
            return "status-sent";
        case NotificationStatus.PENDING:
        case NotificationStatus.CORRESPONDENCE_HRP_UPDATE_PENDING:
            return "status-pending";
        case NotificationStatus.ERROR:
        case NotificationStatus.CORRESPONDENCE_HRP_UPDATE_ERROR:
            return "status-error";
        case NotificationStatus.CANCELED:
            return "status-canceled";
        default:
            return "default";
    }
}

/**
 * Short label inside the summary status pill. HRP correspondence statuses show as
 * {@link NotificationStatus.PENDING} / {@link NotificationStatus.ERROR} for readability;
 * raw values remain in the Raw data JSON tab.
 *
 * @param {unknown} statusRaw
 * @returns {string}
 */
function statusSummaryPillLabel(statusRaw) {
    if (statusRaw == null || String(statusRaw).trim() === "") {
        return "—";
    }

    if (statusRaw === NotificationStatus.CORRESPONDENCE_HRP_UPDATE_PENDING) {
        return NotificationStatus.PENDING;
    }
    if (statusRaw === NotificationStatus.CORRESPONDENCE_HRP_UPDATE_ERROR) {
        return NotificationStatus.ERROR;
    }

    return statusRaw;
}

/**
 * Summary grid (shared `notification-details-sheet__grid*` styles with {@link MemberDetails}): type, status, client, template, plan, sent/scheduled time.
 * Type and status use compact pills; optional `details` refines type/status after load.
 * The sent/scheduled cell uses `getNotificationDisplayTimestamp` and `formatNotificationLocaleDateTime`.
 *
 * @param {object} props
 * @param {Record<string, unknown>} props.listRow Queue list row (`GET /notifications`).
 * @param {Record<string, unknown> | undefined} [props.details] Details payload when loaded (`GET /notifications/details`).
 * @param {string | null} props.queueFilter Active queue tab ({@link QueueFilter}).
 * @returns {import('react').JSX.Element}
 */
const GeneralNotificationInfo = ({ listRow, details, queueFilter }) => {
    const typeRaw = details?.type ?? listRow?.type;
    const statusRaw = details?.status ?? listRow?.status;

    const typeUpper =
        typeRaw != null && String(typeRaw).trim() !== ""
            ? String(typeRaw).trim().toUpperCase()
            : "";

    const channelModifier = CHANNEL_PILL_MODIFIERS.has(typeUpper)
        ? typeUpper.toLowerCase()
        : "default";

    const statusModifier = statusToPillModifier(statusRaw);

    const typeDisplay = typeRaw || "-";

    const statusDisplay = statusSummaryPillLabel(statusRaw);

    const sentOrScheduledLabel =
        getSentOrScheduledTimestampColumnLabelForNotification(
            queueFilter,
            statusRaw,
        );

    return (
        <section
            className="notification-details-sheet__general-info"
            aria-label="Notification summary"
        >
            <div className="notification-details-sheet__grid" role="list">
                <div
                    className="notification-details-sheet__grid-cell"
                    role="listitem"
                >
                    <span className="notification-details-sheet__grid-label">
                        Type
                    </span>
                    <span className="notification-details-sheet__grid-value notification-details-sheet__grid-value--pill-wrap">
                        <span
                            className={`notification-details-sheet__pill notification-details-sheet__pill--compact notification-details-sheet__pill--${channelModifier}`}
                        >
                            {typeDisplay}
                        </span>
                    </span>
                </div>
                <div
                    className="notification-details-sheet__grid-cell"
                    role="listitem"
                >
                    <span className="notification-details-sheet__grid-label">
                        Status
                    </span>
                    <span className="notification-details-sheet__grid-value notification-details-sheet__grid-value--pill-wrap">
                        <span
                            className={`notification-details-sheet__pill notification-details-sheet__pill--compact notification-details-sheet__pill--${statusModifier}`}
                        >
                            {statusDisplay}
                        </span>
                    </span>
                </div>
                <div
                    className="notification-details-sheet__grid-cell"
                    role="listitem"
                >
                    <span className="notification-details-sheet__grid-label">
                        Client
                    </span>
                    <span className="notification-details-sheet__grid-value notification-details-sheet__grid-value--general-plain">
                        {listRow?.client || "—"}
                    </span>
                </div>
                <div
                    className="notification-details-sheet__grid-cell"
                    role="listitem"
                >
                    <span className="notification-details-sheet__grid-label">
                        Template
                    </span>
                    <span className="notification-details-sheet__grid-value notification-details-sheet__grid-value--general-plain">
                        {listRow?.template || "—"}
                    </span>
                </div>
                <div
                    className="notification-details-sheet__grid-cell"
                    role="listitem"
                >
                    <span className="notification-details-sheet__grid-label">
                        Plan
                    </span>
                    <span className="notification-details-sheet__grid-value notification-details-sheet__grid-value--general-plain">
                        {listRow?.plan || "—"}
                    </span>
                </div>
                <div
                    className="notification-details-sheet__grid-cell"
                    role="listitem"
                >
                    <span className="notification-details-sheet__grid-label">
                        {sentOrScheduledLabel}
                    </span>
                    <span className="notification-details-sheet__grid-value notification-details-sheet__grid-value--general-plain">
                        {formatNotificationLocaleDateTime(
                            getNotificationDisplayTimestamp(
                                details ?? listRow,
                                statusRaw,
                            ),
                        )}
                    </span>
                </div>
            </div>
        </section>
    );
};

export default GeneralNotificationInfo;
